import { useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import { showErrorSnackbar } from "@/lib/snackbarUtils";
import { ADD_ELECTION_PAST_PRESIDENT_ASSIGNEE_MUTATION } from "../../mutations";
import { ELECTION_PAST_PRESIDENT_ASSIGNEES_QUERY } from "../../queries";
import PastPresidentPersonSearch, { type PastPresidentPersonSearchNode } from "./PastPresidentPersonSearch";

export type AddPastPresidentDialogProps = {
  open: boolean;
  onClose(): void;
  onAdded?(): void;
};

export default function AddPastPresidentDialog({ open, onClose, onAdded }: AddPastPresidentDialogProps) {
  const [selectedName, setSelectedName] = useState("");
  const [personSearchKey, setPersonSearchKey] = useState(0);

  useEffect(() => {
    if (open) {
      setSelectedName("");
      setPersonSearchKey((key) => key + 1);
    }
  }, [open]);

  const [addAssignee, { loading }] = useMutation(ADD_ELECTION_PAST_PRESIDENT_ASSIGNEE_MUTATION, {
    refetchQueries: [{ query: ELECTION_PAST_PRESIDENT_ASSIGNEES_QUERY }]
  });

  const handlePersonSelect = (person: PastPresidentPersonSearchNode) => {
    setSelectedName(person.displayName);
  };

  const handlePersonClear = () => {
    setSelectedName("");
  };

  const handleAdd = async () => {
    const name = selectedName.trim();
    if (!name || loading) return;

    try {
      const { data: mutationData } = await addAssignee({ variables: { input: { name } } });
      const result = mutationData?.addElectionPastPresidentAssignee;
      if (!result?.success || !result.assignee) {
        showErrorSnackbar("Could not add Past President", { subtitle: result?.message || "Please try again." });
        return;
      }
      onAdded?.();
      onClose();
    } catch {
      showErrorSnackbar("Could not add Past President", { subtitle: "Please try again." });
    }
  };

  return (
    <Dialog
      open={open}
      headline="Add Past President"
      onClosed={onClose}
      hasFormElements
      displayOverflow
      disableClose={loading}
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={loading} testId="add-past-president-dialog-cancel">
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleAdd}
            disabled={!selectedName.trim()}
            loading={loading}
            testId="add-past-president-dialog-save"
          >
            Add
          </Button>
        </>
      }
    >
      <div className="min-w-[360px]">
        <PastPresidentPersonSearch
          key={personSearchKey}
          label="Past President"
          disabled={loading}
          onSelect={handlePersonSelect}
          onClear={handlePersonClear}
        />
      </div>
    </Dialog>
  );
}
